import React, { useState, useEffect } from 'react'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  CubeIcon,
  TagIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/stores/authStore'
import { API_BASE_URL } from '@/services/api'

interface Produto {
  id: number
  code: string
  name: string
  description?: string
  product_type: 'product' | 'service' | 'combo'
  cost_price: number
  sale_price: number
  min_price: number
  formatted_sale_price: string
  formatted_cost_price: string
  margin_percentage: number
  margin_value: number
  track_inventory: boolean
  current_stock: number
  min_stock: number
  max_stock?: number
  unit: string
  is_low_stock: boolean
  is_out_of_stock: boolean
  category_id?: number
  brand?: string
  tax_rate: number
  is_active: boolean
  is_featured: boolean
  image_url?: string
  created_at: string
}

const ProdutosPage: React.FC = () => {
  const { token } = useAuthStore()
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedProduto, setSelectedProduto] = useState<Produto | null>(null)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'product' | 'service' | 'combo'>('all')
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('active')

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  // Form data
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    product_type: 'product',
    cost_price: '',
    sale_price: '',
    min_price: '',
    track_inventory: false,
    current_stock: '',
    min_stock: '',
    max_stock: '',
    unit: 'un',
    category_id: '',
    brand: '',
    tax_rate: '',
    is_active: true,
    is_featured: false,
    image_url: ''
  })

  useEffect(() => {
    fetchProdutos()
  }, [])

  const fetchProdutos = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_BASE_URL}/produtos?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setProdutos(data.produtos || [])
      } else {
        throw new Error('Erro ao carregar produtos')
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const response = await fetch(`${API_BASE_URL}/produtos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: formData.code,
          name: formData.name,
          description: formData.description || null,
          product_type: formData.product_type,
          cost_price: Math.round(parseFloat(formData.cost_price || '0') * 100), // Converter para centavos
          sale_price: Math.round(parseFloat(formData.sale_price) * 100), // Converter para centavos
          min_price: Math.round(parseFloat(formData.min_price || '0') * 100),
          track_inventory: formData.track_inventory,
          current_stock: formData.track_inventory ? parseFloat(formData.current_stock || '0') : 0,
          min_stock: formData.track_inventory ? parseFloat(formData.min_stock || '0') : 0,
          max_stock: formData.max_stock ? parseFloat(formData.max_stock) : null,
          unit: formData.unit,
          category_id: formData.category_id ? parseInt(formData.category_id) : null,
          brand: formData.brand || null,
          tax_rate: formData.tax_rate ? parseFloat(formData.tax_rate) : 0,
          is_active: formData.is_active,
          is_featured: formData.is_featured,
          image_url: formData.image_url || null
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Erro ao criar produto')
      }

      alert('Produto criado com sucesso!')
      setShowCreateModal(false)
      resetForm()
      fetchProdutos()
    } catch (err: any) {
      alert(err.message)
    }
  }

  const handleEdit = (produto: Produto) => {
    setSelectedProduto(produto)
    setFormData({
      code: produto.code,
      name: produto.name,
      description: produto.description || '',
      product_type: produto.product_type,
      cost_price: (produto.cost_price / 100).toFixed(2),
      sale_price: (produto.sale_price / 100).toFixed(2),
      min_price: (produto.min_price / 100).toFixed(2),
      track_inventory: produto.track_inventory,
      current_stock: produto.current_stock.toString(),
      min_stock: produto.min_stock.toString(),
      max_stock: produto.max_stock?.toString() || '',
      unit: produto.unit,
      category_id: produto.category_id?.toString() || '',
      brand: produto.brand || '',
      tax_rate: produto.tax_rate.toString(),
      is_active: produto.is_active,
      is_featured: produto.is_featured,
      image_url: produto.image_url || ''
    })
    setShowEditModal(true)
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedProduto) return

    try {
      const response = await fetch(`${API_BASE_URL}/produtos/${selectedProduto.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: formData.code,
          name: formData.name,
          description: formData.description || null,
          product_type: formData.product_type,
          cost_price: Math.round(parseFloat(formData.cost_price || '0') * 100),
          sale_price: Math.round(parseFloat(formData.sale_price) * 100),
          min_price: Math.round(parseFloat(formData.min_price || '0') * 100),
          track_inventory: formData.track_inventory,
          current_stock: formData.track_inventory ? parseFloat(formData.current_stock || '0') : 0,
          min_stock: formData.track_inventory ? parseFloat(formData.min_stock || '0') : 0,
          max_stock: formData.max_stock ? parseFloat(formData.max_stock) : null,
          unit: formData.unit,
          category_id: formData.category_id ? parseInt(formData.category_id) : null,
          brand: formData.brand || null,
          tax_rate: formData.tax_rate ? parseFloat(formData.tax_rate) : 0,
          is_active: formData.is_active,
          is_featured: formData.is_featured,
          image_url: formData.image_url || null
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Erro ao atualizar produto')
      }

      alert('Produto atualizado com sucesso!')
      setShowEditModal(false)
      setSelectedProduto(null)
      resetForm()
      fetchProdutos()
    } catch (err: any) {
      alert(err.message)
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('Deseja realmente excluir este produto?')) return

    try {
      const response = await fetch(`${API_BASE_URL}/produtos/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) throw new Error('Erro ao excluir produto')

      alert('Produto excluído com sucesso!')
      fetchProdutos()
    } catch (err: any) {
      alert(err.message)
    }
  }

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      product_type: 'product',
      cost_price: '',
      sale_price: '',
      min_price: '',
      track_inventory: false,
      current_stock: '',
      min_stock: '',
      max_stock: '',
      unit: 'un',
      category_id: '',
      brand: '',
      tax_rate: '',
      is_active: true,
      is_featured: false,
      image_url: ''
    })
  }

  // Apply filters
  const filteredProdutos = produtos.filter(produto => {
    const matchesSearch = searchTerm === '' ||
      produto.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      produto.code.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesType = filterType === 'all' || produto.product_type === filterType

    const matchesActive = filterActive === 'all' ||
      (filterActive === 'active' && produto.is_active) ||
      (filterActive === 'inactive' && !produto.is_active)

    return matchesSearch && matchesType && matchesActive
  })

  // Apply pagination
  const totalPages = Math.ceil(filteredProdutos.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedProdutos = filteredProdutos.slice(startIndex, startIndex + itemsPerPage)

  // Statistics - Produtos
  const totalProdutos = produtos.length
  const totalAtivos = produtos.filter(p => p.is_active).length
  const estoqueBaixo = produtos.filter(p => p.is_low_stock).length

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-gray-100 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar produtos</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header com Estatísticas */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Produtos e Serviços</h1>
          <p className="text-gray-600">{totalProdutos} produtos cadastrados ({totalAtivos} ativos)</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <PlusIcon className="w-5 h-5" />
          Novo Produto
        </button>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total de Produtos</p>
              <p className="text-3xl font-bold">{totalProdutos}</p>
              <p className="text-sm">{totalAtivos} ativos</p>
            </div>
            <CubeIcon className="w-12 h-12 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Produtos Físicos</p>
              <p className="text-3xl font-bold">{produtos.filter(p => p.product_type === 'product').length}</p>
            </div>
            <TagIcon className="w-12 h-12 text-green-200" />
          </div>
        </div>

        <div className={`bg-gradient-to-r rounded-xl p-6 text-white ${estoqueBaixo > 0 ? 'from-orange-500 to-orange-600' : 'from-gray-500 to-gray-600'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${estoqueBaixo > 0 ? 'text-orange-100' : 'text-gray-100'}`}>Estoque Baixo</p>
              <p className="text-3xl font-bold">{estoqueBaixo}</p>
              <p className="text-sm">produtos</p>
            </div>
            <ExclamationTriangleIcon className={`w-12 h-12 ${estoqueBaixo > 0 ? 'text-orange-200' : 'text-gray-200'}`} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome ou código..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">Todos os Tipos</option>
            <option value="product">Produtos Físicos</option>
            <option value="service">Serviços</option>
            <option value="combo">Combos</option>
          </select>

          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value as any)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">Todos</option>
            <option value="active">Apenas Ativos</option>
            <option value="inactive">Apenas Inativos</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preço Venda</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Margem</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estoque</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedProdutos.map((produto) => (
                <tr key={produto.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">{produto.code}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{produto.name}</div>
                      {produto.brand && (
                        <div className="text-xs text-gray-500">{produto.brand}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      produto.product_type === 'product' ? 'bg-blue-100 text-blue-800' :
                      produto.product_type === 'service' ? 'bg-purple-100 text-purple-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {produto.product_type === 'product' ? 'Produto' :
                       produto.product_type === 'service' ? 'Serviço' : 'Combo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{produto.formatted_sale_price}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-medium ${produto.margin_percentage > 20 ? 'text-green-600' : 'text-gray-600'}`}>
                      {produto.margin_percentage.toFixed(1)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {produto.track_inventory ? (
                      <div className={`text-sm ${produto.is_low_stock ? 'text-orange-600 font-semibold' : 'text-gray-900'}`}>
                        {produto.current_stock} {produto.unit}
                        {produto.is_low_stock && <span className="text-xs"> (Baixo)</span>}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      produto.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {produto.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(produto)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Editar"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(produto.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Excluir"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando {startIndex + 1} a {Math.min(startIndex + itemsPerPage, filteredProdutos.length)} de {filteredProdutos.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-700">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Novo Produto/Serviço</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código/SKU *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                  <select
                    value={formData.product_type}
                    onChange={(e) => setFormData({...formData, product_type: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="product">Produto Físico</option>
                    <option value="service">Serviço</option>
                    <option value="combo">Combo</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Produto *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preço Custo (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cost_price}
                    onChange={(e) => setFormData({...formData, cost_price: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preço Venda (R$) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.sale_price}
                    onChange={(e) => setFormData({...formData, sale_price: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preço Mínimo (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.min_price}
                    onChange={(e) => setFormData({...formData, min_price: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  checked={formData.track_inventory}
                  onChange={(e) => setFormData({...formData, track_inventory: e.target.checked})}
                  className="rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm text-gray-700 font-medium">Controlar Estoque</span>
              </div>

              {formData.track_inventory && (
                <div className="grid grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estoque Atual</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.current_stock}
                      onChange={(e) => setFormData({...formData, current_stock: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estoque Mínimo</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.min_stock}
                      onChange={(e) => setFormData({...formData, min_stock: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
                    <select
                      value={formData.unit}
                      onChange={(e) => setFormData({...formData, unit: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="un">Unidade (un)</option>
                      <option value="kg">Quilograma (kg)</option>
                      <option value="m">Metro (m)</option>
                      <option value="l">Litro (l)</option>
                      <option value="cx">Caixa (cx)</option>
                      <option value="pc">Peça (pc)</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({...formData, brand: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Taxa Imposto (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({...formData, tax_rate: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Produto Ativo</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Produto Destaque</span>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Criar Produto
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal - Similar ao Create */}
      {showEditModal && selectedProduto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Editar Produto/Serviço</h3>
            <form onSubmit={handleUpdate} className="space-y-4">
              {/* Mesmos campos do Create, mas preenchidos com dados do selectedProduto */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código/SKU *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                  <select
                    value={formData.product_type}
                    onChange={(e) => setFormData({...formData, product_type: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="product">Produto Físico</option>
                    <option value="service">Serviço</option>
                    <option value="combo">Combo</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Produto *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preço Custo (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cost_price}
                    onChange={(e) => setFormData({...formData, cost_price: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preço Venda (R$) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.sale_price}
                    onChange={(e) => setFormData({...formData, sale_price: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preço Mínimo (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.min_price}
                    onChange={(e) => setFormData({...formData, min_price: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  checked={formData.track_inventory}
                  onChange={(e) => setFormData({...formData, track_inventory: e.target.checked})}
                  className="rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm text-gray-700 font-medium">Controlar Estoque</span>
              </div>

              {formData.track_inventory && (
                <div className="grid grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estoque Atual</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.current_stock}
                      onChange={(e) => setFormData({...formData, current_stock: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estoque Mínimo</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.min_stock}
                      onChange={(e) => setFormData({...formData, min_stock: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
                    <select
                      value={formData.unit}
                      onChange={(e) => setFormData({...formData, unit: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="un">Unidade (un)</option>
                      <option value="kg">Quilograma (kg)</option>
                      <option value="m">Metro (m)</option>
                      <option value="l">Litro (l)</option>
                      <option value="cx">Caixa (cx)</option>
                      <option value="pc">Peça (pc)</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({...formData, brand: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Taxa Imposto (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({...formData, tax_rate: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Produto Ativo</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Produto Destaque</span>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Salvar Alterações
                </button>
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setSelectedProduto(null); resetForm(); }}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProdutosPage
